// client/src/components/Footer.js
import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import { FeatureFlagContext } from '../context/FeatureFlagContext';
import { useCategories } from '../context/CategoryContext';
import { useStore } from '../context/StoreContext';

const MAX_FOOTER_CATEGORIES = 6;

const linkSx = {
  color: 'rgba(255,255,255,0.68)',
  textDecoration: 'none',
  display: 'inline-block',
  transition: 'color 0.2s ease',
  '&:hover, &:focus-visible': { color: '#fff' },
};

const FooterLink = ({ to, children }) => (
  <Typography component={Link} to={to} variant="body2" sx={linkSx}>{children}</Typography>
);

const ColumnHeading = ({ children }) => (
  <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1.5 }}>{children}</Typography>
);

const Footer = () => {
  const { isEnabled } = useContext(FeatureFlagContext);
  const { categories } = useCategories();
  const { store } = useStore();
  const footerCategories = categories.filter((c) => c.name !== 'Other').slice(0, MAX_FOOTER_CATEGORIES);

  return (
    <Box
      component="footer"
      className="no-print"
      sx={{
        mt: { xs: 6, md: 9 }, color: '#fff', bgcolor: '#0B0D12',
        backgroundImage: (theme) => `radial-gradient(900px 300px at 0% 0%, ${alpha(theme.palette.primary.main, 0.22)}, transparent 60%)`,
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 6, md: 8 } }}>
        <Box sx={{ display: 'grid', gap: { xs: 4, md: 6 }, gridTemplateColumns: { xs: '1fr 1fr', md: '2fr 1fr 1fr 1fr' } }}>
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' } }}>
            <Typography variant="h5" sx={{ mb: 1.5 }}>{store.name}</Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.68)', maxWidth: 360, mb: 3 }}>
              RC planes, helicopters, model kits and the parts to keep them going, all in one place.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              <AccountBalanceOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption">Pay by EFT</Typography>
            </Stack>
          </Box>
          <Box>
            <ColumnHeading>Categories</ColumnHeading>
            <Stack spacing={1.25} alignItems="flex-start">
              {footerCategories.map((category) => (
                <FooterLink key={category.id} to={`/?${new URLSearchParams({ category: category.name })}`}>{category.name}</FooterLink>
              ))}
            </Stack>
          </Box>
          <Box>
            <ColumnHeading>Shop</ColumnHeading>
            <Stack spacing={1.25} alignItems="flex-start">
              <FooterLink to="/?sort=newest">New Arrivals</FooterLink>
              <FooterLink to="/?sale=true">Specials</FooterLink>
              <FooterLink to="/compare">Compare Products</FooterLink>
              <FooterLink to="/cart">Cart</FooterLink>
            </Stack>
          </Box>
          <Box>
            <ColumnHeading>Account &amp; Support</ColumnHeading>
            <Stack spacing={1.25} alignItems="flex-start">
              <FooterLink to="/about">About &amp; Contact</FooterLink>
              <FooterLink to="/beginner-guide">Beginner's Guide</FooterLink>
              <FooterLink to="/profile">My Account</FooterLink>
              <FooterLink to="/profile?view=orders">My Orders</FooterLink>
              <FooterLink to="/returns">Returns &amp; Refunds</FooterLink>
              {isEnabled('become_seller') && <FooterLink to="/become-seller">Sell on {store.name}</FooterLink>}
              {store.support_email && (
                <Typography component="a" href={`mailto:${store.support_email}`} variant="body2" sx={{ ...linkSx, overflowWrap: 'anywhere' }}>
                  {store.support_email}
                </Typography>
              )}
            </Stack>
          </Box>
        </Box>
        <Divider sx={{ my: 4, borderColor: 'rgba(255,255,255,0.1)' }} />
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          © {new Date().getFullYear()} {store.name}. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
};

export default Footer;
